import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Cpu,
  Lightbulb,
  ShieldCheck,
  Clock,
  Sparkles,
  RefreshCw,
  Play,
  FileDown,
  Network,
  Zap,
} from 'lucide-react';
import { Card } from '../components/ui/Card';
import { DataGrid, Column } from '../components/advanced/DataGrid';
import { Tooltip } from '../components/ui/Tooltip';
import { PageLayout } from '@/components/layout/PageLayout';
import { tabService } from '../services/tabs';
import { api } from '../services/client';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { resolveWorkspaceSessionId } from '@/services/workspaceSession';
import { CATEGORY_BADGE_CLASS, type TabCategory } from '@/utils/tabCategories';
import { TabDetailStrip, type TabPredictionRow, type TabRow } from './tabIntel/TabIntelSubViews';
import {
  filterTabsByTimeRange,
  formatTabTimestamp,
  TIME_RANGE_LABEL,
  transformApiTabs,
  type TimeRange,
} from './tabIntel/tabIntelUtils';
import { scanTabsSecurity } from '@/components/tabs/tabSecurity';

interface IntelligenceMetric {
  label: string;
  value: string | number;
  subtitle: string;
  icon: React.ReactNode;
}

export function TabIntelligenceDashboard() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<TimeRange>('24h');
  const [selectedTab, setSelectedTab] = useState<TabRow | null>(null);
  const [tabs, setTabs] = useState<TabRow[]>([]);
  const [predictions, setPredictions] = useState<TabPredictionRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAnalyzing, setIsAnalyzing] = useState(false);

  const [dashboardMetrics, setDashboardMetrics] = useState({
    activeTabs: 0,
    intelligenceScore: 0,
    securityThreats: 0,
    predictedActions: 0,
    securityScore: null as number | null,
  });

  const filteredTabs = useMemo(() => {
    const inRange = filterTabsByTimeRange(tabs, timeRange);
    return inRange.length > 0 ? inRange : tabs;
  }, [tabs, timeRange]);

  const showingAllTabsDespiteFilter = useMemo(
    () => filterTabsByTimeRange(tabs, timeRange).length === 0 && tabs.length > 0,
    [tabs, timeRange],
  );

  const fetchTabsAndMetrics = useCallback(async () => {
    setIsLoading(true);
    try {
      const sessionId = await resolveWorkspaceSessionId();
      if (!sessionId) {
        setTabs([]);
        setPredictions([]);
        setDashboardMetrics({
          activeTabs: 0,
          intelligenceScore: 0,
          securityThreats: 0,
          predictedActions: 0,
          securityScore: null,
        });
        return;
      }

      const [tabsResult, secResult, predResult] = await Promise.allSettled([
        tabService.getTabsBySession(sessionId, 1, 100),
        api.get<Record<string, unknown>>('security/summary', { params: { session_id: sessionId } }),
        api.get<{ predictions: TabPredictionRow[] }>('smart-tab-prediction/predict', {
          params: { session_id: sessionId },
        }),
      ]);

      const apiTabs = tabsResult.status === 'fulfilled' ? tabsResult.value.tabs : [];
      const secData = secResult.status === 'fulfilled' ? secResult.value.data : {};
      const predData = predResult.status === 'fulfilled' ? predResult.value.data?.predictions || [] : [];

      const transformed = transformApiTabs(apiTabs, predData);
      setTabs(transformed);
      setPredictions(predData);
      void scanTabsSecurity(transformed.map((t) => ({ id: t.id, url: t.url }))).then((results) => {
        setTabs((prev) =>
          prev.map((t) => {
            const info = results.get(t.id);
            return info && info.score !== null ? { ...t, securityScore: info.score } : t;
          }),
        );
      });

      const secThreats = Number(secData.threats_detected ?? secData.threats_found ?? 0);
      const rawSecScore = Number(secData.security_score);
      const secScore = secResult.status === 'fulfilled' && Number.isFinite(rawSecScore) ? rawSecScore : null;

      let intelligenceScore = 50;
      if (transformed.length > 0) {
        intelligenceScore += Math.min(transformed.length * 2, 20);
        if (transformed.length > 15) intelligenceScore -= transformed.length - 15;
        const avgActivity = transformed.reduce((sum, t) => sum + t.activity, 0) / transformed.length;
        intelligenceScore += Math.round(avgActivity * 0.15);
      }
      intelligenceScore += Math.min(predData.length * 5, 15);
      intelligenceScore -= secThreats * 5;
      intelligenceScore = Math.max(0, Math.min(100, intelligenceScore));

      setDashboardMetrics({
        activeTabs: transformed.length,
        intelligenceScore: Math.round(intelligenceScore),
        securityThreats: secThreats,
        predictedActions: predData.length,
        securityScore: secScore,
      });
    } catch (error) {
      console.warn('API unavailable:', error);
      setTabs([]);
      toast.error('Failed to load tabs. Please check your connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  const handleRefreshData = useCallback(async () => {
    toast.info('Refreshing…');
    await fetchTabsAndMetrics();
  }, [fetchTabsAndMetrics]);

  const handleRunFullAnalysis = useCallback(async () => {
    setIsAnalyzing(true);
    toast.info('Running full analysis on all tabs…');
    try {
      if (tabs.length === 0) {
        toast.info('No tabs to analyze.');
        return;
      }
      const results = await Promise.allSettled(
        tabs.map((tab) => api.post(`/tabs-intelligence/analyze/${tab.id}`, {})),
      );
      const ok = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      if (failed === 0) {
        toast.success(`Analysis complete for ${ok} tab${ok === 1 ? '' : 's'}.`);
      } else {
        toast.warning(`Analyzed ${ok} tab(s); ${failed} failed.`);
      }
      await fetchTabsAndMetrics();
    } catch {
      toast.error('Analysis failed.');
    } finally {
      setIsAnalyzing(false);
    }
  }, [fetchTabsAndMetrics, tabs]);

  const handleExportReport = useCallback(() => {
    const reportData = {
      generatedAt: new Date().toISOString(),
      timeRange,
      summary: {
        totalTabs: filteredTabs.length,
        averageActivity:
          Math.round(filteredTabs.reduce((sum, t) => sum + t.activity, 0) / filteredTabs.length) || 0,
        averageSecurityScore: (() => {
          const scored = filteredTabs.filter((t) => t.securityScore !== null);
          return scored.length > 0
            ? Math.round(scored.reduce((sum, t) => sum + (t.securityScore ?? 0), 0) / scored.length)
            : null;
        })(),
      },
      tabs: filteredTabs,
      predictions,
    };
    const blob = new Blob([JSON.stringify(reportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tab-intelligence-report-${new Date().toISOString().split('T')[0]}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported.');
  }, [filteredTabs, predictions, timeRange]);

  const [groupingLoading, setGroupingLoading] = useState(false);
  const [groupingResult, setGroupingResult] = useState<{ group_name: string; count: number; color: string }[] | null>(null);

  const handleSmartGrouping = useCallback(async () => {
    setGroupingLoading(true);
    setGroupingResult(null);
    try {
      const sessionId = await resolveWorkspaceSessionId();
      if (!sessionId) {
        toast.error('No workspace session found.');
        return;
      }
      const suggestions = await tabService.getGroupSuggestions(sessionId);
      const groups = suggestions.map((s) => ({
        group_name: s.groupName,
        count: s.count || s.tabIds.length,
        color: s.color || '#6366f1',
      }));
      setGroupingResult(groups);
      if (groups.length > 0) {
        toast.success(`${groups.length} group suggestion${groups.length === 1 ? '' : 's'} ready`);
      } else {
        toast.info('No grouping suggestions for your current tabs.');
      }
    } catch {
      toast.error('Smart grouping failed.');
    } finally {
      setGroupingLoading(false);
    }
  }, []);

  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityResult, setSecurityResult] = useState<Record<string, unknown> | null>(null);

  const handleSecurityScan = useCallback(async () => {
    setSecurityLoading(true);
    setSecurityResult(null);
    try {
      const sessionId = await resolveWorkspaceSessionId();
      if (!sessionId) {
        toast.error('No workspace session found.');
        return;
      }
      const { data } = await api.get<Record<string, unknown>>('security/summary', {
        params: { session_id: sessionId },
      });
      setSecurityResult(data);
      const threats = Number(data.threats_detected ?? data.threats_found ?? 0);
      toast[threats === 0 ? 'success' : 'warning'](
        threats === 0 ? 'All tabs look safe.' : `${threats} threat(s) detected.`,
      );
      await fetchTabsAndMetrics();
    } catch {
      toast.error('Security scan failed.');
    } finally {
      setSecurityLoading(false);
    }
  }, [fetchTabsAndMetrics]);

  const handleTabRecycling = useCallback(() => {
    navigate('/tabs?section=recycle');
  }, [navigate]);

  useEffect(() => {
    void fetchTabsAndMetrics();
  }, [fetchTabsAndMetrics]);

  const metrics: IntelligenceMetric[] = [
    {
      label: 'Active Tabs',
      value: filteredTabs.length,
      subtitle: TIME_RANGE_LABEL[timeRange],
      icon: <Cpu className="w-5 h-5" />,
    },
    {
      label: 'Intelligence Score',
      value: `${dashboardMetrics.intelligenceScore}%`,
      subtitle: 'tabs, security & predictions',
      icon: <Sparkles className="w-5 h-5" />,
    },
    {
      label: 'Security Threats',
      value: dashboardMetrics.securityThreats,
      subtitle:
        dashboardMetrics.securityScore === null
          ? 'not available'
          : dashboardMetrics.securityThreats > 0
            ? `${dashboardMetrics.securityScore}% workspace score`
            : 'no URL risks found',
      icon: <ShieldCheck className="w-5 h-5" />,
    },
    {
      label: 'Predicted Actions',
      value: dashboardMetrics.predictedActions,
      subtitle: dashboardMetrics.predictedActions > 0 ? 'see Tabs → Predict' : 'none yet',
      icon: <Lightbulb className="w-5 h-5" />,
    },
  ];

  const columns: Column<TabRow>[] = [
    {
      key: 'title',
      header: 'Tab',
      sortable: true,
      minWidth: '200px',
      render: (value, row) => (
        <div className="min-w-0 max-w-[240px]">
          <p className="font-medium text-khiimori-text-primary truncate">{value}</p>
          <p className="text-xs text-khiimori-text-secondary truncate">{row.url}</p>
        </div>
      ),
    },
    {
      key: 'category',
      header: 'Category',
      sortable: true,
      minWidth: '100px',
      render: (value) => (
        <span
          className={`inline-block truncate max-w-[110px] px-2 py-0.5 text-xs font-medium rounded-full ${
            CATEGORY_BADGE_CLASS[value as TabCategory] ?? CATEGORY_BADGE_CLASS.General
          }`}
        >
          {value}
        </span>
      ),
    },
    {
      key: 'activity',
      header: 'Activity',
      sortable: true,
      minWidth: '100px',
      render: (value) => (
        <div className="flex items-center gap-2 w-[90px]">
          <div className="flex-1 bg-khiimori-bg-elevated rounded-full h-1.5">
            <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${value}%` }} />
          </div>
          <span className="text-xs text-khiimori-text-muted w-8 text-right">{value}%</span>
        </div>
      ),
    },
    {
      key: 'securityScore',
      header: 'Security',
      sortable: true,
      minWidth: '72px',
      render: (value: number | null) =>
        value === null ? (
          <span className="text-sm text-khiimori-text-muted" title="Not yet scanned">
            —
          </span>
        ) : (
          <div className="flex items-center gap-1">
            <ShieldCheck
              className={`w-4 h-4 shrink-0 ${
                value > 90 ? 'text-green-500' : value > 70 ? 'text-yellow-500' : 'text-red-500'
              }`}
            />
            <span className="text-sm">{value}</span>
          </div>
        ),
    },
    {
      key: 'relationships',
      header: 'Links',
      sortable: true,
      minWidth: '56px',
      render: (value) => <span className="text-sm">{value}</span>,
    },
    {
      key: 'prediction',
      header: 'Prediction',
      minWidth: '120px',
      render: (value) => (
        <Tooltip content="Open Tabs → Predict for full prediction workflow">
          <span className="flex items-center gap-1 text-xs text-purple-400 max-w-[140px]">
            <Sparkles className="w-3 h-3 shrink-0" />
            <span className="truncate">{value}</span>
          </span>
        </Tooltip>
      ),
    },
    {
      key: 'lastAccessedAt',
      header: 'Last accessed',
      sortable: true,
      minWidth: '108px',
      render: (value) => {
        const formatted = formatTabTimestamp(String(value));
        if (!formatted) return <span className="text-xs text-khiimori-text-muted">—</span>;
        return (
          <div
            className="text-xs text-khiimori-text-muted leading-snug tabular-nums whitespace-nowrap"
            title={formatted.full}
          >
            <div>{formatted.date}</div>
            <div>{formatted.time}</div>
          </div>
        );
      },
    },
  ];

  return (
    <PageLayout title="Tab Intelligence" description="Live tab overview with grouping, security, and workspace insights">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {metrics.map((metric) => (
          <Card key={metric.label} className="p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-xs font-medium text-khiimori-text-muted">{metric.label}</p>
                <p className="text-2xl font-bold text-khiimori-text-primary mt-0.5">{metric.value}</p>
                <p className="text-xs text-khiimori-text-secondary mt-0.5 truncate">{metric.subtitle}</p>
              </div>
              <div className="p-2 rounded-lg bg-primary-500/10 text-primary-400 shrink-0">{metric.icon}</div>
            </div>
          </Card>
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2 justify-between">
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => navigate('/ai?section=relationships')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-khiimori-border bg-khiimori-bg-elevated hover:bg-khiimori-bg-tertiary text-khiimori-text-secondary"
          >
            <Network className="w-3.5 h-3.5" />
            Relationship map
          </button>
          <button
            type="button"
            onClick={() => navigate('/tabs?section=predict')}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-lg border border-khiimori-border bg-khiimori-bg-elevated hover:bg-khiimori-bg-tertiary text-khiimori-text-secondary"
          >
            <Zap className="w-3.5 h-3.5" />
            Tab predictions
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value as TimeRange)}
          className="px-3 py-1.5 text-sm bg-khiimori-bg-elevated border border-khiimori-border rounded-lg"
        >
          <option value="1h">Last hour</option>
          <option value="24h">Last 24 hours</option>
          <option value="7d">Last 7 days</option>
          <option value="30d">Last 30 days</option>
        </select>
        <button
          type="button"
          onClick={() => void handleRefreshData()}
          disabled={isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-khiimori-border bg-khiimori-bg-elevated hover:bg-khiimori-bg-tertiary disabled:opacity-50"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
        <button
          type="button"
          onClick={handleExportReport}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-khiimori-border bg-khiimori-bg-elevated hover:bg-khiimori-bg-tertiary"
        >
          <FileDown className="w-3.5 h-3.5" />
          Export
        </button>
        </div>
      </div>

      <div className="space-y-4">
        {showingAllTabsDespiteFilter && (
          <p className="text-xs text-amber-400 px-1">
            No tabs updated in {TIME_RANGE_LABEL[timeRange].toLowerCase()} — showing all workspace tabs.
          </p>
        )}

        {selectedTab && <TabDetailStrip tab={selectedTab} onClose={() => setSelectedTab(null)} />}

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full" />
          </div>
        ) : filteredTabs.length === 0 ? (
          <Card className="p-10 text-center text-sm text-khiimori-text-muted">
            No tabs in this workspace. Sync tabs from the browser extension.
          </Card>
        ) : (
          <DataGrid
            data={filteredTabs}
            columns={columns}
            searchable
            tableLayout="auto"
            onRowClick={(tab) => setSelectedTab(tab)}
            groupBy="category"
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <Card hoverable className="p-4 cursor-pointer" onClick={() => void handleSmartGrouping()}>
            <div className="flex items-center gap-2 mb-2">
              {groupingLoading ? (
                <RefreshCw className="w-4 h-4 text-purple-400 animate-spin" />
              ) : (
                <Sparkles className="w-4 h-4 text-purple-400" />
              )}
              <h3 className="font-semibold text-sm">Smart Grouping</h3>
            </div>
            <p className="text-xs text-khiimori-text-muted">
              {groupingResult?.length
                ? groupingResult.map((g) => g.group_name).join(', ')
                : 'Organize tabs into logical groups'}
            </p>
          </Card>

          <Card hoverable className="p-4 cursor-pointer" onClick={() => void handleSecurityScan()}>
            <div className="flex items-center gap-2 mb-2">
              {securityLoading ? (
                <RefreshCw className="w-4 h-4 text-green-400 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4 text-green-400" />
              )}
              <h3 className="font-semibold text-sm">Security Scan</h3>
            </div>
            <p className="text-xs text-khiimori-text-muted">
              {securityResult
                ? `${securityResult.threats_detected ?? securityResult.threats_found ?? 0} threat(s)`
                : 'Scan all active tabs'}
            </p>
          </Card>

          <Card hoverable className="p-4 cursor-pointer" onClick={handleTabRecycling}>
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4 text-orange-400" />
              <h3 className="font-semibold text-sm">Tab Recycling</h3>
            </div>
            <p className="text-xs text-khiimori-text-muted">Close inactive tabs and save for later</p>
          </Card>
        </div>

        <button
          type="button"
          onClick={() => void handleRunFullAnalysis()}
          disabled={isAnalyzing || tabs.length === 0}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
        >
          {isAnalyzing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
          {isAnalyzing ? 'Analyzing…' : 'Run full analysis'}
        </button>
      </div>
    </PageLayout>
  );
}
